import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { TownEvent, WeatherNow } from '@shared/types';
import { HOME } from '@shared/home';
import { dateKeyInZone, formatEventTimeRange, shortEventLocation } from '@shared/events';
import { appendEnglish } from '@shared/ptEn';
import { Bilingual } from '@/components/Bilingual';
import { formatTempF, sunClocks } from '@/lib/formatting';

function dateLabel(dateKey: string): string {
  return new Date(`${dateKey}T12:00:00`).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

export function TodayCard({
  today,
  weather,
  events,
  loading,
}: {
  today: string | null;
  weather: WeatherNow;
  events: TownEvent[];
  loading: boolean;
}) {
  const [more, setMore] = useState(false);
  const sunset = sunClocks(weather.sunrise, weather.sunset).sunset;
  const dateKey = today ?? dateKeyInZone(new Date(), HOME.timezone);
  const todays = events.filter((event) => event.date === dateKey);
  const visible = more ? todays : todays.slice(0, 2);

  return (
    <section className="card today-card" aria-label="Today">
      <h2 className="today-card__title">Today</h2>
      <p className="today-card__date">{dateLabel(dateKey)}</p>
      <p className="today-card__weather">
        {weather.summary} · {formatTempF(weather.tempC)}
      </p>
      {sunset ? <p className="today-card__sunset">Sunset {sunset}</p> : null}
      <h3 className="today-card__events-title">Today’s events</h3>
      {loading ? <p className="muted">Loading today’s events…</p> : null}
      {!loading && todays.length === 0 ? <p className="muted">Nothing listed today.</p> : null}
      <ul className="today-card__events">
        {visible.map((event) => {
          const where = shortEventLocation(event.location);
          const meta = [formatEventTimeRange(event), where ? appendEnglish(where, where) : ''].filter(Boolean).join(' · ');
          return (
            <li key={event.id}>
              <Link to={`/events#event-${event.id}`}>
                <Bilingual text={event.title} english={event.titleEn} />
              </Link>
              {meta ? <span className="today-card__meta">{meta}</span> : null}
            </li>
          );
        })}
      </ul>
      {todays.length > 2 ? (
        <button type="button" className="rb-more" aria-expanded={more} onClick={() => setMore((value) => !value)}>
          {more ? 'Show less' : 'Show more'}
        </button>
      ) : null}
    </section>
  );
}
